import React, { useState, useEffect, useMemo } from "react";
import { motion } from "framer-motion";
import { X, Plus, Trash2, Search, Save, ArrowLeft, Loader2 } from "lucide-react";
import { useForm, useFieldArray } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod"; // Ensure this is used for validation schemas

import { cn } from "../../lib/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Separator } from "../ui/separator";
import { ScrollArea} from "../ui/scrollbar";
import { Form ,  FormControl,
    FormDescription,
    FormField,
    FormItem,
    FormLabel,
    FormMessage,} from "../ui/formdata";
import { Textarea} from "../ui/textarea";
import { useQuery } from "react-query";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import assessmentList from "../../apis/assessmentListApi";
import { Select ,  SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,} from "../ui/select";
import { defaultForm, initialRubricsValue, predefinedCategories ,phonepredefinedCategories } from "./Rubricsmodel";
import { useToast } from "../ui/use-toast";
import DashboardApi from "../../apis/dashboard/index";
import useStore from "../../store/useStore";
import SettingApi from "../../apis/SettingApi";
import isEqual from "lodash/isEqual";

// Form schema for validation
const followUpQuestionSchema = z.object({
  condition: z
    .string()
    .min(2, { message: "Condition must be at least 2 characters" }),
  question: z
    .string()
    .min(2, { message: "Question must be at least 2 characters" }),
});

const staticQuestionSchema = z.object({
  question: z
    .string()
    .min(2, { message: "Question must be at least 2 characters" }),
  follow_up: z.array(followUpQuestionSchema).optional().default([]),
});

const formSchema = z.object({
  category: z
    .string()
    .min(2, { message: "Category must be at least 2 characters" }),
  displayName: z
    .string()
    .min(2, { message: "Display name must be at least 2 characters" }),
  description: z
    .string()
    .min(10, { message: "Description must be at least 10 characters" }),
  question_guidelines: z.array(z.string()).default([]),
  specific_questions: z.array(staticQuestionSchema).optional().default([]),
  evaluation_guidelines: z.array(z.string()).default([]),
});



const RubricModalSplit = ({
  open = false,
  onOpenChange = () => {},
 
  assessment,
  onSave = () => {},
}) => {
  // const [rubrics, setRubrics] = useState(initialRubricsValue);
const {toast}=useToast();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedRubric, setSelectedRubric] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const token = useStore((state) => state.token);
  const examId=assessment.examId;
  const name=assessment.type;
  // Set the first rubric as selected by default
  const {data:defaultValue,isLoading:defaultLoading,isError} = useQuery({
    queryKey: ["defaultValue",name],
    queryFn: () => SettingApi.getDefaultValue(name)
  });


  const { data: rubricData, isLoading: rubricLoading, isError: rubricError,refetch } = useQuery({
    queryKey: ["rubricsData"],
    queryFn: () => assessmentList.show(token, null)
  });
 

  // Filter rubric data based on examId, or use initialRubrics if null
  const filteredRubrics = useMemo(() => {

    if ( !defaultValue) return  []; // Fallback to initial rubrics
if(!rubricData) return defaultValue?.data?.value;
if(rubricData.length===0) return defaultValue?.data?.value;

    let FilterData = rubricData.filter((rubric) => rubric.examId === examId);


  
    let rubricFilterData=FilterData[0].rubrics;

    if (searchTerm.trim()) {
      rubricFilterData = rubricFilterData.filter(
        (rubric) =>
          rubric.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
          rubric.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    return rubricFilterData ? rubricFilterData : defaultValue?.data?.value;
  }, [rubricData, examId, searchTerm,refetch,defaultValue]);

useEffect(()=>{


  if (isEqual(filteredRubrics, defaultValue?.data?.value)) {
    const defaultFunction=async()=>await SettingApi.update({token,examId:assessment.examId,interview_rubrics:defaultValue?.data?.value})
  defaultFunction();
    // This checks if both arrays/objects have the same content
  }
  
},[filteredRubrics])
  useEffect(() => {
    if (filteredRubrics && filteredRubrics.length > 0 && !selectedRubric && !isAdding) {
      setSelectedRubric(filteredRubrics[0]);
    }
  }, [filteredRubrics, selectedRubric, isAdding,]);

 

  // Predefined categories for the form


  // Get default questions based on category
  const getDefaultQuestions = (category) => {
    if (category === "Static Mandatory Questions") {
      return [
        {
          question: "Are you looking for job change?",
          follow_up: [
            {
              condition: "yes",
              question: "What location are you open for?",
            },
            {
              condition: "yes",
              question: "What is your current CTC?",
            },
            {
              condition: "yes",
              question: "What is your expected CTC?",
            },
            {
              condition: "yes",
              question: "What is your notice period?",
            },
          ],
        },
      ];
    }
    return [];
  };

  // Initialize form with default values or selected rubric data
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: selectedRubric
      ? {
          category: selectedRubric.category,
          displayName: selectedRubric.displayName,
          description: selectedRubric.description,
          question_guidelines: selectedRubric.question_guidelines,
          specific_questions: selectedRubric.specific_questions||[],
          evaluation_guidelines: selectedRubric.evaluation_guidelines,
         
        }
      : {
          category: "Technical Programming Fundamentals",
          displayName: "Tech Skills",
          description:
            "Assess advanced programming concepts, code quality, and maintainability.",
          question_guidelines: [
            "Ask a minimum of 2 questions and a maximum of 5 questions to understand their tech fundamentals.",
            "Increase the difficulty level based on the candidate's answers.",
          ],
          specific_questions: [
          ]
          ,
          evaluation_guidelines: [
            "Score based on coding efficiency, readability, and maintainability.",
            "Assess the candidate's ability to write clean, modular, and efficient code.",
          ],
       
        },
  });

  // Reset form when selected rubric changes
  useEffect(() => {
    if (selectedRubric && isEditing) {
      form.reset({
        category: selectedRubric.category,
        displayName: selectedRubric.displayName,
        description: selectedRubric.description,
        question_guidelines: selectedRubric.question_guidelines,
        specific_questions: selectedRubric.specific_questions || [],
        evaluation_guidelines: selectedRubric.evaluation_guidelines,
      
      });
    }
  }, [selectedRubric, isEditing, form]);

  // Field arrays for form
  const questionGuidelines = useFieldArray({
    control: form.control,
    name: "question_guidelines",
  });

  const specificQuestions = useFieldArray({
    control: form.control,
    name: "specific_questions",
  });

  const evaluationGuidelines = useFieldArray({
    control: form.control,
    name: "evaluation_guidelines",
  });

 
  // Update display name when category changes
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === "category") {
        const selectedCategory =phonepredefinedCategories.find(
          (c) => c.category === value.category,
        );
        if (selectedCategory) {
          form.setValue("displayName", selectedCategory.displayName, {
            shouldValidate: true,
            shouldDirty: true,
          });

          // If it's the static mandatory questions category, set default questions
          if (value.category === "Static Mandatory Questions") {
            form.setValue(
              "static_questions",
              getDefaultQuestions(value.category),
              {
                shouldValidate: true,
                shouldDirty: true,
              },
            );
          }
        }
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  const handleAddRubric = () => {
    setSelectedRubric(null);
    setIsAdding(true);
    setIsEditing(false);
    form.reset(defaultForm);
  };

  const handleEditRubric = (rubric) => {
    setSelectedRubric(rubric);
    setIsEditing(true);
    setIsAdding(false);
    form.reset({
      category: rubric.category,
      displayName: rubric.displayName,
      description: rubric.description,
      question_guidelines: rubric.question_guidelines,
      specific_questions: rubric.specific_questions,
      evaluation_guidelines: rubric.evaluation_guidelines,
     
    });
  };

  const handleDeleteRubric = async (category) => {
    try {
      let updatedRubrics = [...filteredRubrics];
 
         updatedRubrics =updatedRubrics.filter((rubric) =>rubric.category!== category);
  if(updatedRubrics.length<1){
    toast({
      title: "Cannot Delete: At Least One Rubric Required",
      variant: "destructive",
      description: "An error occurred while deleting the rubric. You must have at least one rubric.",
    });
    
  }
  else{
      // If the deleted rubric was selected, update selectedRubric
      if (selectedRubric?.category=== category) {
        setSelectedRubric(updatedRubrics.length > 0 ? updatedRubrics[0] : null);
        setIsEditing(false);
      }
  
      const response = await SettingApi.update({
        token,
        examId: assessment.examId,
        interview_rubrics: updatedRubrics,
      });
  
      if (!response) {
        toast({
          title: "Error deleting rubric",
          variant: "destructive",
          description: "Failed to delete rubric. Please try again.",
        });
        return;
      }
  
      // Update state
    
      await refetch();
  
      toast({
        title: "Rubric deleted successfully",
      });
    }
    } catch (error) {
      console.error("Error deleting rubric:", error);
      toast({
        title: "Error deleting rubric",
        variant: "destructive",
        description: "An error occurred while deleting the rubric.",
      });
    }
  };
  
  const handleCancelEdit = () => {
    setIsEditing(false);
    setIsAdding(false);
  };

  const handleSubmit = async (data) => {
    setIsSubmitting(true);
   
  
    try {
  
      if(!filteredRubrics){
    
        const newRubric = { ...data };
       const updatefilteredRubrics=[newRubric];
       filteredRubrics=updatefilteredRubrics;
        const data1=await SettingApi.update({token,examId:assessment.examId,interview_rubrics:updatefilteredRubrics})
        if(!data1){
          toast({
            title: "Not able to add Rubrics",
            variant:"destructive",
            description:"Please Try Again. "
  
          })
        }
        if(data1?.data){
          await refetch();
          toast({
            title: "Rubrics Edited Successfully",
          
          })
        }
     
        setSelectedRubric(filteredRubrics[0]);
      }else{
        let updatedRubrics = [...filteredRubrics];
      if (isEditing && selectedRubric) {
      
        // Update existing rubric
         updatedRubrics =updatedRubrics.map((rubric) =>
          rubric.category=== selectedRubric.category
            ? { ...data}
            : rubric,
        );
      
        const data1=await SettingApi.update({token,examId:assessment.examId,interview_rubrics:updatedRubrics})
        if(!data1){
          toast({
            title: "Not able to add Rubrics",
            variant:"destructive",
            description:"Please Try Again. "
  
          })
        }
        if(data1?.data){
          await refetch();
          toast({
            title: "Rubrics Edited Successfully",
          
          })
        }
        setSelectedRubric(filteredRubrics[0]);
      } else {
        // Add new rubric
        const presentRubrics = updatedRubrics.filter((rubric) =>
          rubric.category === data.category
           
        );
        if(presentRubrics.length>0){
          toast({
            title: "Rubric already exists",
            variant:"destructive",
            description:"This rubric is already present. You can edit the existing rubric if needed. "
          })
          return;
        }
        
        const newRubric = { ...data };
        updatedRubrics.push(newRubric);
        const data1=await SettingApi.update({token,examId:assessment.examId,interview_rubrics:updatedRubrics})
      if(!data1){
        toast({
          title: "Not able to add Rubrics",
          variant:"destructive",
          description:"Please Try Again. "

        })
      }
      if(data1?.data){
        await refetch();
        toast({
          title: "Rubrics Added Successfully",
         
        })
       
      }
        // setRubrics([...rubrics, newRubric]);
        setSelectedRubric(filteredRubrics[0]);
      }
    }
      setIsEditing(false);
      setIsAdding(false);
    
  
    } catch (error) {
      console.error("Error saving rubric:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const getCategoryColor = (category) => {
    if (!category || typeof category !== "string") {
      return "bg-gray-100 text-gray-800"; // Default fallback
    }
    if (
      category.includes("Introduction") ||
      category.includes("Communication")
    ) {
      return "bg-blue-100 text-blue-800";
    } else if (category.includes("Problem-Solving")) {
      return "bg-green-100 text-green-800";
    } else if (category.includes("Technical")) {
      return "bg-yellow-100 text-yellow-800";
    } else if (
      category.includes("Generative AI") ||
      category.includes("LLMs")
    ) {
      return "bg-indigo-100 text-indigo-800";
    } else if (category.includes("NLP") || category.includes("Deep Learning")) {
      return "bg-purple-100 text-purple-800";
    } else if (category.includes("API") || category.includes("Development")) {
      return "bg-pink-100 text-pink-800";
    } else if (category.includes("Static") || category.includes("Mandatory")) {
      return "bg-amber-100 text-amber-800";
    } else {
      return "bg-gray-100 text-gray-800";
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-white sm:max-w-md md:max-w-3xl lg:max-w-5xl p-0 h-[92vh] lg:h-[90vh] flex flex-col">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.3 }}
          className="w-full flex flex-col h-full"
        >
          <DialogHeader className="px-6 pt-4 pb-2 flex gap-2 items-center  bg-white z-10">
            <DialogTitle className="text-xl font-semibold">
              Interview Rubrics
            </DialogTitle>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => onOpenChange(false)}
              className="h-4 w-4 rounded-full"
            >
              <X className="h-4 w-4" />
            </Button>
          </DialogHeader>

          <div className="flex flex-1  w-full h-full  flex-col lg:flex-row overflow-y-auto">
            {/* Left sidebar with rubric list */}
            <div className="w-full lg:w-2/5 h-1/3 lg:h-full border-r border-gray-200 flex flex-col">
              <div className="p-4 border-b border-gray-200">
                <div className="relative mb-3">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Search rubrics..."
                    className="pl-8"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
                <Button
                  onClick={handleAddRubric}
                  className="w-full flex items-center justify-center gap-1"
                >
                  <Plus className="h-4 w-4" />
                  Add New Rubric
                </Button>
              </div>

              <ScrollArea className="flex-1">
                <div className="p-3 space-y-2">
                  {filteredRubrics && filteredRubrics.length>0 && filteredRubrics.map((rubric) =>{ return (
                    <div
                      key={rubric.category}
                      className={cn(
                        "p-3 rounded-md cursor-pointer transition-colors",
                        selectedRubric?.id === rubric.id && !isAdding
                          ? "bg-blue-50 border border-blue-200"
                          : "hover:bg-gray-100 border border-transparent",
                      )}
                      onClick={() => {
                        if (isEditing || isAdding) {
                          // Show confirmation if there are unsaved changes
                          if (form.formState.isDirty) {
                            if (
                              window.confirm(
                                "You have unsaved changes. Are you sure you want to discard them?",
                              )
                            ) {
                              setSelectedRubric(rubric);
                              setIsEditing(false);
                              setIsAdding(false);
                            }
                          } else {
                            setSelectedRubric(rubric);
                            setIsEditing(false);
                            setIsAdding(false);
                          }
                        } else {
                          setSelectedRubric(rubric);
                        }
                      }}
                    >
                      <div className="flex items-center justify-between mb-1">
                        <Badge
                          className={cn(
                            "font-normal text-xs",
                            getCategoryColor(rubric.category),
                          )}
                        >
                          {rubric.displayName}
                        </Badge>
                      </div>
                      <h3 className="font-medium text-sm truncate">
                        {rubric.category}
                      </h3>
                      <p className="text-xs text-gray-500 line-clamp-2 mt-1">
                        {rubric.description}
                      </p>
                    </div>
                  )})}

                  { filteredRubrics && filteredRubrics.length === 0 && !isAdding && (
                    <div className="text-center py-8">
                      <p className="text-gray-500 text-sm">
                        {searchTerm
                          ? "No matching rubrics found"
                          : "No rubrics created yet"}
                      </p>
                      <Button
                        onClick={handleAddRubric}
                        variant="outline"
                        size="sm"
                        className="mt-2"
                      >
                        <Plus className="h-4 w-4 mr-1" /> Add Rubric
                      </Button>
                    </div>
                  )}
                </div>
              </ScrollArea>
            </div>

            {/* Right content area */}
            <div className="flex-1 flex flex-col overflow-y-auto  w-full h-full max-h-[48vh] lg:max-h-[74vh]" >
              {isEditing || isAdding ? (
                <div className="p-6 flex flex-col h-full">
                  <div className="flex justify-between items-center mb-4">
                    <div className="flex items-center">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelEdit}
                        className="mr-2"
                      >
                        <ArrowLeft className="h-4 w-4 mr-1" /> Back
                      </Button>
                      <h2 className="text-xl font-semibold">
                        {isAdding ? "Add New Rubric" : "Edit Rubric"}
                      </h2>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  {/* Form content */}
                  <div className="flex-1 overflow-y-auto pr-2">
                    <Form {...form}>
                      <form
                        onSubmit={form.handleSubmit(handleSubmit)}
                        className="flex flex-col gap-2"
                      >
                        <FormField
                          control={form.control}
                          name="category"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Category</FormLabel>
                              <Select
                                onValueChange={field.onChange}
                                defaultValue={field.value}
                              >
                                <FormControl>
                                  <SelectTrigger className="w-full">
                                    <SelectValue placeholder="Select a category" />
                                  </SelectTrigger>
                                </FormControl>
                                <SelectContent>
                                  {phonepredefinedCategories.map((cat) => (
                                    <SelectItem
                                      key={cat.category}
                                      value={cat.category}
                                    >
                                      <div className="flex items-center">
                                        {cat.displayName}
                                      </div>
                                    </SelectItem>
                                  ))}
                                </SelectContent>
                              </Select>
                              <FormDescription className="text-xs">
                                Select the category for this rubric
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="displayName"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Display Name</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="e.g., Technical Skills"
                                  {...field}
                                />
                              </FormControl>
                              <FormDescription className="text-xs">
                                This name will be displayed to interviewers
                              </FormDescription>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <FormField
                          control={form.control}
                          name="description"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Description</FormLabel>
                              <FormControl>
                                <Textarea
                                  placeholder="Describe what this rubric evaluates..."
                                  className="min-h-[100px]"
                                  {...field}
                                />
                              </FormControl>
                              <FormMessage />
                            </FormItem>
                          )}
                        />

                        <div className>
                          <h3 className="text-base font-medium mb-2 flex items-center">
                            Question Guidelines
                          </h3>
                          <Card className="bg-blue-50 border-blue-200 mb-4">
                            <CardHeader className="pb-2 pt-4">
                              <CardTitle className="text-sm font-medium text-blue-800 flex justify-between items-center">
                                <span>Guidelines for asking questions</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => questionGuidelines.append("")}
                                  className="h-8 text-xs bg-blue-100 hover:bg-blue-200 border-blue-300"
                                >
                                  <Plus className="h-3 w-3 mr-1" /> Add
                                  Guideline
                                </Button>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className=" flex flex-col gap-2 justify-center">
                                {questionGuidelines.fields.length === 0 && (
                                  <p className="text-sm text-blue-500 italic">
                                    Add guidelines for asking questions
                                  </p>
                                )}

                                {questionGuidelines.fields.map(
                                  (field, index) => (
                                    <div
                                      key={field.id}
                                      className="flex items-center gap-2"
                                    >
                                      <FormField
                                        control={form.control}
                                        name={`question_guidelines.${index}`}
                                        render={({ field }) => (
                                          <FormItem className="flex-1 mb-0">
                                            <FormControl>
                                              <Input
                                                placeholder="e.g., Ask a minimum of 2 questions..."
                                                className="text-sm"
                                                {...field}
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-500 hover:text-red-500"
                                        onClick={() =>
                                          questionGuidelines.remove(index)
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ),
                                )}
                              </div>
                            </CardContent>
                          </Card>

                          <h3 className="text-base font-medium mb-2 flex items-center">
                            Specific Questions
                          </h3>
                          <Card className="bg-amber-50 border-amber-200">
                            <CardHeader className="pb-2 pt-4">
                              <CardTitle className="text-sm font-medium text-amber-800 flex justify-between items-center">
                                <span>Specific questions to ask</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    specificQuestions.append({
                                      question: "", // Ensure default value is an empty string
                                      follow_up: [], // Ensure default value is an empty array
                                    })
                                  }
                                  className="h-8 text-xs bg-amber-100 hover:bg-amber-200 border-amber-300"
                                >
                                  <Plus className="h-3 w-3 mr-1" /> Add Question
                                </Button>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-4">
                                {specificQuestions.fields.length === 0 && (
                                  <p className="text-sm text-amber-500 italic">
                                    Add specific questions to ask during the interview
                                  </p>
                                )}

                                {specificQuestions.fields.map((field, index) => {
                                  const followUpQuestions =
                                    form.getValues(`specific_questions.${index}.follow_up`) || []; // Ensure follow_up is an array

                                  return (
                                    <div
                                      key={field.id}
                                      className="border border-amber-200 rounded-md p-3 bg-amber-100"
                                    >
                                      <div className="flex items-center gap-2 mb-2">
                                        <FormField
                                          control={form.control}
                                          name={`specific_questions.${index}.question`} // Correct field name
                                          render={({ field }) => (
                                            <FormItem className="flex-1 mb-0">
                                              <FormLabel className="text-amber-800">Question</FormLabel>
                                              <FormControl>
                                                <Input
                                                  placeholder="e.g., Are you looking for job change?"
                                                  className="text-sm"
                                                  value={field.value || ""} // Ensure controlled input
                                                  onChange={field.onChange}
                                                />
                                              </FormControl>
                                              <FormMessage />
                                            </FormItem>
                                          )}
                                        />
                                        <Button
                                          type="button"
                                          variant="ghost"
                                          size="icon"
                                          className="h-8 w-8 text-gray-500 hover:text-red-500 mt-6"
                                          onClick={() => specificQuestions.remove(index)}
                                        >
                                          <Trash2 className="h-4 w-4" />
                                        </Button>
                                      </div>

                                      <div className="ml-4 mt-3 border-l-2 border-amber-300 pl-3">
                                        <div className="flex justify-between items-center mb-2">
                                          <h5 className="text-xs font-medium text-amber-800">
                                            Follow-up Questions
                                          </h5>
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() =>
                                              form.setValue(`specific_questions.${index}.follow_up`, [
                                                ...followUpQuestions,
                                                {
                                                  condition: "", // Ensure default value is an empty string
                                                  question: "", // Ensure default value is an empty string
                                                },
                                              ])
                                            }
                                            className="h-7 text-xs bg-amber-50 hover:bg-amber-100 border-amber-200"
                                          >
                                            <Plus className="h-3 w-3 mr-1" /> Add Follow-up
                                          </Button>
                                        </div>

                                        {followUpQuestions.map((_, followUpIndex) => (
                                          <div
                                            key={followUpIndex}
                                            className="flex flex-col gap-2 mb-2 bg-amber-50 p-2 rounded-md"
                                          >
                                            <div className="flex gap-2">
                                              <FormField
                                                control={form.control}
                                                name={`specific_questions.${index}.follow_up.${followUpIndex}.condition`} // Correct field name
                                                render={({ field }) => (
                                                  <FormItem className="flex-1 mb-0">
                                                    <FormLabel className="text-xs text-amber-800">
                                                      Condition
                                                    </FormLabel>
                                                    <FormControl>
                                                      <Input
                                                        placeholder="e.g., yes"
                                                        className="text-sm h-8"
                                                        value={field.value || ""} // Ensure controlled input
                                                        onChange={field.onChange}
                                                      />
                                                    </FormControl>
                                                    <FormMessage />
                                                  </FormItem>
                                                )}
                                              />
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="h-8 w-8 text-gray-500 hover:text-red-500 mt-6"
                                                onClick={() =>
                                                  form.setValue(
                                                    `specific_questions.${index}.follow_up`,
                                                    followUpQuestions.filter((_, i) => i !== followUpIndex)
                                                  )
                                                }
                                              >
                                                <Trash2 className="h-4 w-4" />
                                              </Button>
                                            </div>
                                            <FormField
                                              control={form.control}
                                              name={`specific_questions.${index}.follow_up.${followUpIndex}.question`} // Correct field name
                                              render={({ field }) => (
                                                <FormItem className="flex-1 mb-0">
                                                  <FormLabel className="text-xs text-amber-800">
                                                    Follow-up Question
                                                  </FormLabel>
                                                  <FormControl>
                                                    <Input
                                                      placeholder="e.g., What location are you open for?"
                                                      className="text-sm"
                                                      value={field.value || ""} // Ensure controlled input
                                                      onChange={field.onChange}
                                                    />
                                                  </FormControl>
                                                  <FormMessage />
                                                </FormItem>
                                              )}
                                            />
                                          </div>
                                        ))}

                                        {followUpQuestions.length === 0 && (
                                          <p className="text-xs text-amber-500 italic">
                                            Add follow-up questions based on candidate responses
                                          </p>
                                        )}
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                            </CardContent>
                          </Card>

                          <h3 className="text-base font-medium mb-2 flex items-center">
                            Evaluation Guidelines
                          </h3>
                          <Card className="bg-purple-50 border-purple-200 mb-4">
                            <CardHeader className="pb-2 pt-4">
                              <CardTitle className="text-sm font-medium text-purple-800 flex justify-between items-center">
                                <span>Guidelines for evaluation</span>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() =>
                                    evaluationGuidelines.append("")
                                  }
                                  className="h-8 text-xs bg-purple-100 hover:bg-purple-200 border-purple-300"
                                >
                                  <Plus className="h-3 w-3 mr-1" /> Add
                                  Guideline
                                </Button>
                              </CardTitle>
                            </CardHeader>
                            <CardContent>
                              <div className="space-y-2">
                                {evaluationGuidelines.fields.length === 0 && (
                                  <p className="text-sm text-purple-500 italic">
                                    Add guidelines for evaluating responses
                                  </p>
                                )}

                                {evaluationGuidelines.fields.map(
                                  (field, index) => (
                                    <div
                                      key={field.id}
                                      className="flex items-center gap-2"
                                    >
                                      <FormField
                                        control={form.control}
                                        name={`evaluation_guidelines.${index}`}
                                        render={({ field }) => (
                                          <FormItem className="flex-1 mb-0">
                                            <FormControl>
                                              <Input
                                                placeholder="e.g., Score based on clarity of explanation..."
                                                className="text-sm"
                                                {...field}
                                              />
                                            </FormControl>
                                            <FormMessage />
                                          </FormItem>
                                        )}
                                      />
                                      <Button
                                        type="button"
                                        variant="ghost"
                                        size="icon"
                                        className="h-8 w-8 text-gray-500 hover:text-red-500"
                                        onClick={() =>
                                          evaluationGuidelines.remove(index)
                                        }
                                      >
                                        <Trash2 className="h-4 w-4" />
                                      </Button>
                                    </div>
                                  ),
                                )}
                              </div>
                            </CardContent>
                          </Card>
                         

                         
                        </div>

                        <div className=" bg-white pt-4 pb-2 flex justify-end gap-2 mb-2">
                          <Button
                            type="button"
                            variant="outline"
                            onClick={handleCancelEdit}
                          >
                            Cancel
                          </Button>
                          <Button
                            type="submit"
                            disabled={isSubmitting}
                            className="flex items-center gap-1"
                          >
                            {isSubmitting ? (
                              <span className="animate-pulse">Saving...</span>
                            ) : (
                              <>
                                <Save className="h-4 w-4 mr-1" />
                                {isEditing ? "Update" : "Save"} Rubric
                              </>
                            )}
                          </Button>
                        </div>
                      </form>
                    </Form>
                  </div>
                </div>
              ) : selectedRubric ? (
                <div className="p-6 flex flex-col h-full">
                  <div className="flex justify-between items-start mb-4">
                    <div>
                      <Badge
                        className={cn(
                          "mb-2",
                          getCategoryColor(selectedRubric.category),
                        )}
                      >
                        {selectedRubric.displayName}
                      </Badge>
                      <h2 className="text-2xl font-bold">
                        {selectedRubric.category}
                      </h2>
                      <p className="text-gray-600 mt-1">
                        {selectedRubric.description}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleEditRubric(selectedRubric)}
                      >
                        Edit Rubric
                      </Button>
                      <Button
                        variant="destructive"
                        size="sm"
                        onClick={() => handleDeleteRubric(selectedRubric.category)}
                      >
                        Delete
                      </Button>
                    </div>
                  </div>

                  <Separator className="my-4" />

                  <ScrollArea className="flex-1">
                    <div className="space-y-6">
                      {/* Question Guidelines */}
                      <div>
                        <h3 className="text-lg font-medium mb-2">
                          Question Guidelines
                        </h3>
                        <div className="space-y-2">
                          {selectedRubric.question_guidelines.map(
                            (guideline, index) => (
                              <div
                                key={index}
                                className="p-3 bg-blue-50 rounded-md text-sm"
                              >
                                {guideline}
                              </div>
                            ),
                          )}
                          {selectedRubric.question_guidelines.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                              No question guidelines defined
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Specific Questions */}
                      <div>
                        <h3 className="text-lg font-medium mb-2">
                          Specific Questions
                        </h3>
                        <div className="space-y-2"> 
                          {selectedRubric.specific_questions.map(
                            (item, index) =>(
                              <div
                                key={index}
                                className="p-3 bg-green-50 rounded-md text-sm"
                              >
                              <div className="p-3 bg-amber-50 font-medium">
                                      {item.question||item}
                                    </div>
                                    {item.follow_up &&
                                      item.follow_up.length > 0 && (
                                        <div className="p-3 pt-0">
                                          <p className="text-xs font-medium text-amber-800 mt-3 mb-2">
                                            Follow-up Questions:
                                          </p>
                                          <div className="space-y-2 ml-4 border-l-2 border-amber-200 pl-3">
                                            {item.follow_up.map(
                                              (followUp, fIndex) => (
                                                <div
                                                  key={fIndex}
                                                  className="text-sm"
                                                >
                                                  <span className="text-amber-700 font-medium">
                                                    If {followUp.condition}:
                                                  </span>{" "}
                                                  {followUp.question}
                                                </div>
                                              ),
                                            )}
                                          </div>
                                        </div>
                                      )}
                                
                              </div>
                              
                            )
                          )}
                          {selectedRubric.specific_questions.length === 0 && (
                            <p className="text-gray-500 text-sm italic">
                              No specific questions defined
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Evaluation Guidelines */}
                      <div>
                        <h3 className="text-lg font-medium mb-2">
                          Evaluation Guidelines
                        </h3>
                        <div className="space-y-2">
                          {selectedRubric.evaluation_guidelines.map(
                            (guideline, index) => (
                              <div
                                key={index}
                                className="p-3 bg-purple-50 rounded-md text-sm"
                              >
                                {guideline}
                              </div>
                            ),
                          )}
                          {selectedRubric.evaluation_guidelines.length ===
                            0 && (
                            <p className="text-gray-500 text-sm italic">
                              No evaluation guidelines defined
                            </p>
                          )}
                        </div>
                      </div>

                      {/* Static Questions */}
                   
                    </div>
                  </ScrollArea>
                </div>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <div className="text-center p-6">
                    <p className="text-gray-500 mt-1 max-w-md">
                      Select a rubric from the list or create a new one to view
                      details
                    </p>
                    <Button onClick={handleAddRubric} className="mt-4">
                      <Plus className="h-4 w-4 mr-2" /> Create New Rubric
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </motion.div>
      </DialogContent>
    </Dialog>
  );
};

export default RubricModalSplit;
